import React, { useEffect, useState } from "react";
import api, { inr } from "@/lib/api";
import { toast } from "sonner";
import { Plus, X, RotateCcw, ArrowRightCircle, Undo2, Trash2, Eye, AlertCircle } from "lucide-react";
import DetailPopup from "@/components/DetailPopup";
import WhatsAppShortcuts from "@/components/WhatsAppShortcuts";
import AdminDeleteButton from "@/components/AdminDeleteButton";
import { usePagination } from "@/components/Paginator";

const emptyItem = () => ({ product: "", color: "", qty: 1, notes: "" });

export default function Samples() {
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState({ total_collected: 0, refunded: 0, pending: 0, open_count: 0 });
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({
    customer_name: "",
    phone: "",
    dc_type: "refundable",
    amount_collected: 0,
    pending_amount: 0,
    expected_return_at: "",
    notes: "",
    items: [emptyItem()],
  });
  const [refunding, setRefunding] = useState(null);
  const [refundAmt, setRefundAmt] = useState(0);
  const [popup, setPopup] = useState(null);

  const load = async () => {
    setItems((await api.get("/samples")).data);
    setSummary((await api.get("/samples-summary")).data);
  };
  useEffect(() => { load(); }, []);

  // Reveal / hide the amount field based on type
  const showAmount = form.dc_type === "refundable" || form.dc_type === "paid";
  // "yet_to_be_paid" — customer promised to pay; can be closed later
  const isPendingPayment = (s) => s.dc_type === "yet_to_be_paid" && !s.dc_closed;

  const markPaid = async (s) => {
    const expected = s.pending_amount || 0;
    const paid = Number(window.prompt(`Enter amount received (expected ₹${expected}):`, expected) || 0);
    if (paid <= 0) return;
    try {
      await api.put(`/samples/${s.id}`, { amount_collected: paid, dc_type: "paid", pending_amount: 0 });
      toast.success("Payment recorded"); load();
    } catch { toast.error("Failed"); }
  };

  const closeWithoutPayment = async (id) => {
    if (!window.confirm("Close DC as delivered without collecting payment? A payment-pending flag will remain on this record.")) return;
    try {
      await api.put(`/samples/${id}`, { return_status: "converted", dc_closed: true });
      toast.success("Closed with payment pending"); load();
    } catch { toast.error("Failed"); }
  };

  const addLine = () => setForm(f => ({ ...f, items: [...f.items, emptyItem()] }));
  const rmLine = (i) => setForm(f => ({ ...f, items: f.items.filter((_, idx) => idx !== i) }));
  const setLine = (i, k, v) => setForm(f => ({
    ...f, items: f.items.map((it, idx) => idx === i ? { ...it, [k]: v } : it)
  }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.customer_name) return toast.error("Customer name is required");
    const clean = form.items.filter(i => i.product);
    if (clean.length === 0) return toast.error("Add at least one item");
    try {
      await api.post("/samples", { ...form, items: clean });
      toast.success("Sample DC recorded");
      setForm({ customer_name: "", phone: "", dc_type: "refundable", amount_collected: 0, pending_amount: 0, expected_return_at: "", notes: "", items: [emptyItem()] });
      setShowAdd(false); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const doRefund = async (id) => {
    try {
      await api.post(`/samples/${id}/refund`, { refund_amount: Number(refundAmt) });
      toast.success("Sample closed — refund logged"); setRefunding(null); setRefundAmt(0); load();
    } catch { toast.error("Failed"); }
  };
  const doConvert = async (id) => {
    try { await api.post(`/samples/${id}/convert-to-order`); toast.success("Sample converted — DC closed"); load(); }
    catch { toast.error("Failed"); }
  };
  const reopen = async (id) => {
    try { await api.put(`/samples/${id}`, { return_status: "pending", dc_closed: false, refund_amount: 0, returned_at: null, refund_at: null }); toast.success("Reopened"); load(); }
    catch { toast.error("Failed"); }
  };

  const itemsSummary = (s) => {
    if (s.items && s.items.length) return `${s.items.length} item${s.items.length>1?"s":""} · ${s.items.reduce((a,b)=>a+Number(b.qty||0),0)} pcs`;
    return `${s.product || "-"} · ${s.qty || 0} pcs`;
  };

  const { pageItems, controls } = usePagination(items, 20);

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <div className="label-tiny mb-1">Samples / DC</div>
          <h1 className="font-display text-3xl tracking-tight">Samples out</h1>
          <p className="text-sm text-[#75706A] mt-1">Multi-item DCs, 5-day return timer, click a row to view details.</p>
        </div>
        <button className="btn-primary flex items-center gap-2" onClick={()=>setShowAdd(v=>!v)} data-testid="samples-add-btn">
          {showAdd ? <><X size={16}/> Cancel</> : <><Plus size={16}/> New sample DC</>}
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Kpi label="Deposits collected" value={inr(summary.total_collected)} />
        <Kpi label="Refunded" value={inr(summary.refunded)} tone="text-[#4A7258]" />
        <Kpi label="Pending refund" value={inr(summary.pending)} tone={summary.pending>0?"text-[#B83A3A]":""} />
        <Kpi label="Open DCs" value={summary.open_count} />
      </div>

      {showAdd && (
        <form onSubmit={submit} className="card-surface p-5 space-y-4 fade-up" data-testid="samples-form">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div><div className="label-tiny mb-1">Customer *</div>
              <input required className="input-field" value={form.customer_name} onChange={e=>setForm({...form,customer_name:e.target.value})} data-testid="sample-customer-name"/></div>
            <div><div className="label-tiny mb-1">Phone <span className="text-[10px] normal-case">(for WhatsApp reminders)</span></div>
              <input className="input-field" value={form.phone} onChange={e=>setForm({...form,phone:e.target.value})} data-testid="sample-phone"/></div>
            <div><div className="label-tiny mb-1">Type</div>
              <select className="input-field" value={form.dc_type} onChange={e=>setForm({...form,dc_type:e.target.value})} data-testid="sample-type">
                <option value="refundable">Refundable deposit</option>
                <option value="paid">Paid sample</option>
                <option value="free">Free sample</option>
                <option value="yet_to_be_paid">Yet to be paid (delivered on trust)</option>
              </select>
            </div>
            {showAmount && (
              <div><div className="label-tiny mb-1">{form.dc_type === "paid" ? "Amount received" : "Deposit amount"}</div>
                <input type="number" className="input-field" value={form.amount_collected} onChange={e=>setForm({...form,amount_collected:Number(e.target.value)})}/></div>
            )}
            {form.dc_type === "yet_to_be_paid" && (
              <>
                <div>
                  <div className="label-tiny mb-1">Amount pending (to be collected)</div>
                  <input type="number" className="input-field" value={form.pending_amount}
                    onChange={e=>setForm({...form, pending_amount: Number(e.target.value)})}
                    data-testid="sample-pending-amount"
                    placeholder="e.g. 1500"/>
                </div>
                <div className="md:col-span-1 flex items-end">
                  <div className="text-xs text-[#B83A3A] bg-[#FBEBE7] px-2 py-1.5 rounded-md flex items-center gap-1.5 w-full">
                    <AlertCircle size={12}/> Payment will be flagged pending.
                  </div>
                </div>
              </>
            )}
            <div><div className="label-tiny mb-1">Expected return (default 5 days)</div>
              <input type="date" className="input-field" value={form.expected_return_at} onChange={e=>setForm({...form,expected_return_at:e.target.value})}/></div>
            <div className="md:col-span-2"><div className="label-tiny mb-1">Notes</div>
              <input className="input-field" value={form.notes} onChange={e=>setForm({...form,notes:e.target.value})} placeholder="Optional"/></div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <div className="label-tiny">Items in this DC</div>
              <button type="button" onClick={addLine} className="btn-ghost text-xs flex items-center gap-1" data-testid="sample-add-line"><Plus size={12}/> Add item</button>
            </div>
            <div className="space-y-2">
              {form.items.map((it, i) => (
                <div key={i} className="grid grid-cols-12 gap-2 items-end border border-[#EAE5DB] rounded-md p-2 bg-[#FBF8F1]">
                  <div className="col-span-4"><div className="label-tiny mb-1">Product *</div>
                    <input required={i===0} className="input-field" value={it.product} onChange={e=>setLine(i,"product",e.target.value)} data-testid={`sample-item-product-${i}`}/></div>
                  <div className="col-span-2"><div className="label-tiny mb-1">Color</div>
                    <input className="input-field" value={it.color} onChange={e=>setLine(i,"color",e.target.value)}/></div>
                  <div className="col-span-1"><div className="label-tiny mb-1">Qty</div>
                    <input type="number" className="input-field" value={it.qty || ""} onChange={e=>setLine(i,"qty",Number(e.target.value)||0)} data-testid={`sample-item-qty-${i}`}/></div>
                  <div className="col-span-4"><div className="label-tiny mb-1">Item notes</div>
                    <input className="input-field" value={it.notes} onChange={e=>setLine(i,"notes",e.target.value)}/></div>
                  <div className="col-span-1 flex justify-end">
                    {form.items.length > 1 && <button type="button" className="p-2 text-[#B83A3A] hover:bg-[#F7DFDF] rounded" onClick={()=>rmLine(i)} data-testid={`sample-remove-line-${i}`}><Trash2 size={14}/></button>}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="flex justify-end"><button type="submit" className="btn-primary" data-testid="sample-submit">Save DC</button></div>
        </form>
      )}

      <div className="card-surface overflow-x-auto">
        <table className="data-table">
          <thead><tr><th>Customer</th><th>Items</th><th>Type</th><th>Deposit</th><th>Return by</th><th>Status</th><th>Actions</th></tr></thead>
          <tbody>
            {items.length === 0 && <tr><td colSpan={7} className="p-6 text-center text-[#75706A]">No samples out.</td></tr>}
            {pageItems.map(s => (
              <React.Fragment key={s.id}>
                <tr data-testid={`sample-row-${s.id}`} className="cursor-pointer" onClick={()=>setPopup(s)}>
                  <td className="font-medium">{s.customer_name}</td>
                  <td className="text-xs">{itemsSummary(s)}</td>
                  <td>
                    {s.dc_type === "yet_to_be_paid"
                      ? <span className="badge badge-warn">Yet to pay</span>
                      : <span className="text-xs">{s.dc_type}</span>}
                  </td>
                  <td className="mono">{inr(s.amount_collected||0)}</td>
                  <td className="mono">{s.expected_return_at || "—"}</td>
                  <td>
                    {s.return_status === "returned" && <span className="badge badge-success">Refunded {inr(s.refund_amount||0)}</span>}
                    {s.return_status === "converted" && (
                      s.dc_type === "yet_to_be_paid" && Number(s.amount_collected||0) === 0
                        ? <span className="badge badge-danger">Payment pending {s.pending_amount ? inr(s.pending_amount) : ""}</span>
                        : <span className="badge badge-info">Converted</span>
                    )}
                    {s.return_status === "pending" && (s.overdue
                      ? <span className="badge badge-danger">Overdue {s.days_outstanding}d</span>
                      : (s.dc_type === "yet_to_be_paid" && s.pending_amount
                        ? <span className="badge badge-warn">Owes {inr(s.pending_amount)}</span>
                        : <span className="badge badge-warn">Pending</span>))}
                  </td>
                  <td onClick={e=>e.stopPropagation()}>
                    <div className="flex gap-1.5 flex-wrap items-center">
                      {s.return_status === "pending" && (
                        <>
                          <button className="btn-ghost text-xs flex items-center gap-1" onClick={()=>{setRefunding(s.id); setRefundAmt(s.amount_collected||0);}} data-testid={`sample-refund-${s.id}`}><RotateCcw size={11}/>Refund</button>
                          <button className="btn-ghost text-xs flex items-center gap-1" onClick={()=>doConvert(s.id)}><ArrowRightCircle size={11}/>Converted</button>
                          {s.dc_type === "yet_to_be_paid" && (
                            <button className="btn-ghost text-xs flex items-center gap-1 text-[#B83A3A]" onClick={()=>closeWithoutPayment(s.id)} data-testid={`sample-close-unpaid-${s.id}`}>Close (unpaid)</button>
                          )}
                        </>
                      )}
                      {s.return_status === "converted" && s.dc_type === "yet_to_be_paid" && Number(s.amount_collected||0) === 0 && (
                        <button className="btn-primary text-xs" onClick={()=>markPaid(s)} data-testid={`sample-mark-paid-${s.id}`}>Log payment</button>
                      )}
                      {s.return_status !== "pending" && (
                        <button className="btn-ghost text-xs flex items-center gap-1 text-[#75706A]" onClick={()=>reopen(s.id)}><Undo2 size={11}/>Reopen</button>
                      )}
                      {s.phone && (
                        <WhatsAppShortcuts phone={s.phone} name={s.customer_name} variant="compact" testid={`wa-sample-${s.id}`} />
                      )}
                      <AdminDeleteButton endpoint={`/samples/${s.id}`} label={`sample DC for ${s.customer_name}`} onDone={load} testid={`delete-sample-${s.id}`}/>
                    </div>
                  </td>
                </tr>
                {refunding === s.id && (
                  <tr>
                    <td colSpan={7} className="bg-[#F7F1E1]">
                      <div className="flex items-center gap-2 p-3">
                        <span className="text-sm">Refund amount:</span>
                        <input type="number" className="input-field w-40" value={refundAmt} onChange={e=>setRefundAmt(Number(e.target.value))} autoFocus/>
                        <button className="btn-primary text-xs" onClick={()=>doRefund(s.id)} data-testid={`sample-refund-confirm-${s.id}`}>Close DC & log refund</button>
                        <button className="btn-ghost text-xs" onClick={()=>setRefunding(null)}>Cancel</button>
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
        {controls && <div className="px-2 pb-3">{controls}</div>}
      </div>

      <DetailPopup open={!!popup} onClose={()=>setPopup(null)}
        subtitle="Sample DC" title={popup?.customer_name || ""}>
        {popup && <>
          <div><span className="text-[#75706A]">Status:</span> <span className="badge">{popup.return_status}</span></div>
          <div><span className="text-[#75706A]">Type:</span> {popup.dc_type} · Deposit <span className="mono">{inr(popup.amount_collected||0)}</span></div>
          {popup.dc_type === "yet_to_be_paid" && popup.pending_amount > 0 && (
            <div className="p-2 bg-[#FBEBE7] rounded-md text-xs text-[#7C2626]">
              <b>Amount owed:</b> <span className="mono">{inr(popup.pending_amount)}</span>
            </div>
          )}
          <div><span className="text-[#75706A]">Return by:</span> {popup.expected_return_at || "—"}</div>
          <div className="mt-3 pt-3 border-t border-[#E7E1D3]">
            <div className="label-tiny mb-1">Items ({popup.items?.length || (popup.product?1:0)})</div>
            {popup.items?.length ? popup.items.map((it,i)=>(
              <div key={i} className="text-xs py-1 flex justify-between">
                <span>{it.product}{it.color && ` · ${it.color}`}{it.notes && ` · ${it.notes}`}</span>
                <span className="mono">{it.qty}</span>
              </div>
            )) : <div className="text-xs">{popup.product} · <span className="mono">{popup.qty}</span></div>}
          </div>
          {popup.notes && <div className="text-xs text-[#75706A] mt-2">Notes: {popup.notes}</div>}
        </>}
      </DetailPopup>
    </div>
  );
}

const Kpi = ({label, value, tone}) => (
  <div className="card-surface p-4">
    <div className="label-tiny">{label}</div>
    <div className={`font-display text-2xl font-bold mt-1 ${tone||""}`}>{value}</div>
  </div>
);
